import type Phaser from "phaser";
import { t } from "../../../data/strings";
import type { InfiniteWarState, IwCardId } from "../infinite-war-rules";
import {
  cardInfo,
  formatCardCombatLine,
  formatCardFaceStats,
} from "./card-info";
import {
  BOARD,
  HAND_MAX,
  HAND_UI,
  factionDamageColor,
  factionDamageColorCss,
} from "./layout";
import { spendableMana } from "./mana-hud";

export type CardsHudHost = {
  scene: Phaser.Scene;
  cardGfx: Phaser.GameObjects.Graphics;
  cardTexts: Phaser.GameObjects.Text[];
};

export type DrawCardsOpts = {
  /** Aim / targeting copy — shown inside detail; scene must hide standalone hint. */
  aimHint?: string | null;
};

/** Depth: detail text above card face text; gfx cleared each frame. */
const DEPTH = {
  cardText: 14,
  detailText: 15,
} as const;

export function drawCards(
  host: CardsHudHost,
  state: InfiniteWarState,
  selected: IwCardId | null,
  opts: DrawCardsOpts = {},
): void {
  const g = host.cardGfx;
  g.clear();
  destroyCardTexts(host);

  // Detail first (below cards in Y); cards drawn after so any pixel overlap favors cards.
  if (selected) {
    drawSelectedDetail(host, selected, opts.aimHint ?? null);
  }

  const hand = state.cards.south.hand;
  for (let i = 0; i < HAND_MAX; i += 1) {
    const x = HAND_UI.originX + i * HAND_UI.pitch;
    const card = hand[i];
    const slotX = x - HAND_UI.cardW / 2;
    if (!card) {
      g.fillStyle(0x171c28, 0.85).fillRoundedRect(
        slotX,
        HAND_UI.y,
        HAND_UI.cardW,
        HAND_UI.cardH,
        8,
      );
      g.lineStyle(1, 0x3a4558, 0.7).strokeRoundedRect(
        slotX,
        HAND_UI.y,
        HAND_UI.cardW,
        HAND_UI.cardH,
        8,
      );
      continue;
    }
    const info = cardInfo(card);
    const ready = spendableMana(state, "south") >= info.cost;
    const isSelected = selected === card;
    const dmgCss = factionDamageColorCss(info.team);
    g.fillStyle(isSelected ? 0x3d76a2 : ready ? 0x203b5b : 0x202938).fillRoundedRect(
      slotX,
      HAND_UI.y,
      HAND_UI.cardW,
      HAND_UI.cardH,
      8,
    );
    g.lineStyle(
      isSelected ? 3 : 1,
      isSelected ? 0xf8d88a : ready ? 0x8ac7ff : 0x52627a,
      1,
    ).strokeRoundedRect(slotX, HAND_UI.y, HAND_UI.cardW, HAND_UI.cardH, 8);

    const nameColor = ready ? "#f8fafc" : "#718096";
    const statCss = ready ? dmgCss : "#718096";
    host.cardTexts.push(
      host.scene.add
        .text(x, HAND_UI.y + 4, t(info.labelKey), {
          fontFamily: "Malgun Gothic, sans-serif",
          fontSize: "10px",
          align: "center",
          color: nameColor,
          wordWrap: { width: HAND_UI.cardW - 8 },
        })
        .setOrigin(0.5, 0)
        .setDepth(DEPTH.cardText),
    );
    host.cardTexts.push(
      host.scene.add
        .text(x, HAND_UI.y + 18, formatCardFaceStats(info.combat), {
          fontFamily: "Malgun Gothic, sans-serif",
          fontSize: "9px",
          align: "center",
          color: statCss,
          wordWrap: { width: HAND_UI.cardW - 8 },
        })
        .setOrigin(0.5, 0)
        .setDepth(DEPTH.cardText),
    );
    host.cardTexts.push(
      host.scene.add
        .text(x, HAND_UI.y + HAND_UI.cardH - 10, String(info.cost), {
          fontFamily: "Georgia, serif",
          fontSize: "13px",
          align: "center",
          color: ready ? "#9ee7ff" : "#718096",
        })
        .setOrigin(0.5)
        .setDepth(DEPTH.cardText),
    );
  }
}

function drawSelectedDetail(
  host: CardsHudHost,
  card: IwCardId,
  aimHint: string | null,
): void {
  const g = host.cardGfx;
  const info = cardInfo(card);
  const dmgColor = factionDamageColor(info.team);
  const dmgCss = factionDamageColorCss(info.team);
  const panelX = BOARD.x;
  const panelW = BOARD.w;
  const panelY = HAND_UI.detailY;
  const panelH = HAND_UI.detailH;
  const padX = 12;
  const padY = 5;
  // Hard stop: panel must end above card row.
  if (panelY + panelH > HAND_UI.y - 4) {
    return;
  }
  g.fillStyle(0x121826, 0.96).fillRoundedRect(panelX, panelY, panelW, panelH, 8);
  g.lineStyle(2, dmgColor, 0.95).strokeRoundedRect(panelX, panelY, panelW, panelH, 8);

  const titleX = panelX + padX;
  const titleY = panelY + padY;
  const costW = 72;
  host.cardTexts.push(
    host.scene.add
      .text(titleX, titleY, t(info.labelKey), {
        fontFamily: "Malgun Gothic, sans-serif",
        fontSize: "12px",
        color: "#f8fafc",
        wordWrap: { width: panelW - padX * 2 - costW },
      })
      .setDepth(DEPTH.detailText),
  );
  host.cardTexts.push(
    host.scene.add
      .text(panelX + panelW - padX, titleY, t("iw.card.detail.cost", { n: info.cost }), {
        fontFamily: "Malgun Gothic, sans-serif",
        fontSize: "12px",
        color: "#9ee7ff",
      })
      .setOrigin(1, 0)
      .setDepth(DEPTH.detailText),
  );

  const stats = formatCardCombatLine(info.combat);
  const midY = panelY + 22;
  if (aimHint) {
    // Targeting: stats + aim — skip long desc so lines stay exclusive.
    if (stats) {
      host.cardTexts.push(
        host.scene.add
          .text(titleX, midY, stats, {
            fontFamily: "Malgun Gothic, sans-serif",
            fontSize: "11px",
            color: dmgCss,
            wordWrap: { width: panelW - padX * 2 },
          })
          .setDepth(DEPTH.detailText),
      );
    }
    host.cardTexts.push(
      host.scene.add
        .text(titleX, panelY + panelH - 14, aimHint, {
          fontFamily: "Malgun Gothic, sans-serif",
          fontSize: "11px",
          color: "#f8d88a",
          wordWrap: { width: panelW - padX * 2 },
        })
        .setDepth(DEPTH.detailText),
    );
  } else {
    host.cardTexts.push(
      host.scene.add
        .text(titleX, midY, t(info.descKey), {
          fontFamily: "Malgun Gothic, sans-serif",
          fontSize: "10px",
          color: "#cbd5e1",
          wordWrap: { width: panelW - padX * 2 },
        })
        .setDepth(DEPTH.detailText),
    );
    if (stats) {
      host.cardTexts.push(
        host.scene.add
          .text(titleX, panelY + panelH - 14, stats, {
            fontFamily: "Malgun Gothic, sans-serif",
            fontSize: "11px",
            color: dmgCss,
            wordWrap: { width: panelW - padX * 2 },
          })
          .setDepth(DEPTH.detailText),
      );
    }
  }
}

export function destroyCardTexts(host: CardsHudHost): void {
  for (const text of host.cardTexts) text.destroy();
  host.cardTexts = [];
}
